import type { DentalOfficeShiftCardStatus } from '@tempmee/react-blocks';
import { IDashboardJob } from '~/domain/DashboardJobs';
import type { TShiftCardData } from '~/types/ShiftCard';
import { getProfessionalName } from '~/utils/professional';
import { computeHours, formatRate, parseCardDate, resolveProfession } from '~/utils/shiftCard';

/**
 * Translates a Shift Overview `IDashboardJob` into props for the DS
 * `DentalOfficeShiftCard`. The tab owns the lifecycle `status` (each tab is a
 * fixed status), so it is passed in rather than derived here.
 */
export const mapDashboardJobToShiftCard = (
  job: IDashboardJob,
  status: DentalOfficeShiftCardStatus,
): TShiftCardData => {
  const data: TShiftCardData = {
    status,
    date: parseCardDate(job.dashboardJobApi?.local_date),
    startTime: job.startTime,
    endTime: job.endTime,
    hours: computeHours(job.startTime, job.endTime),
    rate: formatRate(job.rate),
    profession: resolveProfession(job.professionName, job.subProfession),
    office: job.office?.name ?? '',
    interview: Boolean(job.dashboardJobApi?.isOnsiteInterview),
  };

  if (status === 'confirm-candidates') {
    data.candidates = (job.multipleCounters ?? []).map((counter) => ({
      name: getProfessionalName(counter?.candidate),
      avatarUrl: counter?.candidate?.profile_image,
      isFavorite: Boolean(counter?.isFavorite),
    }));
  } else if (status === 'open') {
    data.invitedCount = job.dashboardJobApi?.favoriteInvites?.length ?? 0;
  } else if (job.professional) {
    const name = `${job.professional.firstName ?? ''} ${job.professional.lastName ?? ''}`.trim();
    const avatarUrl = job.professional.profileImage || undefined;
    if (name || avatarUrl) {
      data.person = {
        ...(name ? { name } : {}),
        ...(avatarUrl ? { avatarUrl } : {}),
      };
    }
  }

  return data;
};
